import { Component, Input, Output, EventEmitter } from '@angular/core';
import { Item } from '../entity/entities';
import { TableColumn } from '../entity/table/tableColumn';
import { ColumnTypes } from '../entity/enums';

@Component({
  selector: 'gs-table-data',
  templateUrl: './tableRow.control.html',
  styleUrls: ['./tableRow.control.scss'],
})
export class TableRowControl {
  @Input() item: Item;
  @Input() items;
  @Input() columns: Array<TableColumn>;
  @Input() col;
  @Input() readOnly = false;
  @Input() rowIndex;
  @Input() showSelectAll;
  @Input() expanded;
  @Input() wide = false; //defines will table be wide as parent element

  @Output() rowAction = new EventEmitter<any>();
  @Output() rowDataChanged = new EventEmitter<any>();
  @Output() selectRow = new EventEmitter<any>();
  @Output() selectCell = new EventEmitter<any>();

  Math = Math;
  ColumnTypes = ColumnTypes;
  showModal: boolean = false;

  handleChange($event, item, columnName: string, column: TableColumn) {
    const isActionColumn =
      column &&
      (column.type == ColumnTypes.DeleteAction ||
        column.type == ColumnTypes.EditAction ||
        column.type == ColumnTypes.ConditionalEditActionButton ||
        column.type == ColumnTypes.Button ||
        column.type == ColumnTypes.EditActionText ||
        column.type == ColumnTypes.BoolShowAction ||
        column.type == ColumnTypes.CurrencyWithAction ||
        column.type == ColumnTypes.BadgeWithAction);

    // #29055: a disabled action column is only styled with .disabled, whose pointer-events: none
    // stops the mouse but not the keyboard - the <button> carries no disabled attribute, so it
    // stays focusable and Enter/Space still fires (click). On a closed business date that let the
    // action run anyway. Guarded here, at the single funnel all 34 template call sites go through,
    // rather than in each template. Deliberately only the action branch: rowDataChanged carries
    // input edits, which are already gated at the input level.
    if (isActionColumn && this.isRowActionDisabled(item, column)) return;

    if (isActionColumn)
      this.rowAction.emit({
        originalEvent: $event,
        row: item.data,
        column: column,
        columnName: columnName,
        item: item,
      });
    else
      this.rowDataChanged.emit({
        originalEvent: $event,
        row: item ? item.data : undefined,
        column: column,
        columnName: columnName,
        item: item,
      });
  }

  // #29083: a per-row answer to "is this action disabled?", used by BOTH the guard above and
  // the label branch of ColumnTypes.Button in the template, so the two cannot drift - which is
  // how #29055 happened in the first place.
  //
  // Mirrors the icon branch's [disabled] expression with two deliberate omissions:
  //
  //  - NOT col.readonly. brkdowncol_teamup is readonly:true by design (gs.table.builder sets it
  //    for every dynamic column, and calculatedData re-asserts it), so including readonly here
  //    would disable Team Up outright, on every day.
  //
  //  - NOT col.disabledParameterReference. That flag is not column-agnostic: CurrencyWithAction
  //    uses it to disable its gs-input-decimal while its action button stays live, so blocking
  //    on it would break the fee action buttons. parameters.column[name].disabled, by contrast,
  //    means exactly "the action in this column, in this row, is disabled" everywhere it is
  //    written (Verify, AssignLocations, Delete, payout_pay, payout_load, ...).
  isRowActionDisabled(item, column: TableColumn): boolean {
    if (!column) return false;
    if (column.disabled) return true;

    const perRow =
      item && item.parameters && item.parameters.column
        ? item.parameters.column[column.name]
        : undefined;

    return !!(perRow && perRow.disabled);
  }

  handleRadioButtonChange(
    $event,
    item,
    columnName: string,
    column: TableColumn,
    value
  ) {
    this.rowDataChanged.emit({
      originalEvent: $event,
      row: item.data,
      column: column,
      columnName: columnName,
      selectedValue: value,
      item: item,
    });
  }

  onSelectRow(row) {
    this.selectRow.emit(row);
  }

  onSelectCell(event) {
    this.selectCell.emit(event);
  }

  validField(item: Item, colName) {
    let validationInfo = item.validationInfo.find(
      (vi) => vi.fieldName == colName
    );
    if (validationInfo) return validationInfo.valid;

    return true;
  }

  getValue(data, name) {
    if (name) {
      if (name.indexOf('.') == -1) return data[name];
      else {
        let props = name.split('.');
        let l = 0;
        let prop;
        while (props.length > l) {
          if (l == 0 && data[props[l]] == undefined) return;
          else prop = prop ? prop[props[l]] : data[props[l]];
          l++;
        }
        return prop;
      }
    }
  }

  getIcon(item, column) {
    let iconValuePair = column.iconValueSet.find(
      (iv) => iv.value == item.data[column.name]
    );
    if (iconValuePair)
      return column.iconValueSet.find(
        (iv) => iv.value == item.data[column.name]
      ).icon;
  }

  getIconColor(item, column) {
    let iconValuePair = column.iconValueSet.find(
      (iv) => iv.value == item.data[column.name]
    );
    if (iconValuePair)
      return column.iconValueSet.find(
        (iv) => iv.value == item.data[column.name]
      ).color;
  }

  isHidden(item, col) {
    return (
      item.parameters?.column &&
      item.parameters?.column[col.name] &&
      item.parameters?.column[col.name].hide
    );
  }

  showDialog($event, item, columnName: string, column: TableColumn) {
    this.showModal = true;
  }
}
